import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, ShieldCheck, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function VerifyEmailPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (token) verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-email/${token}`);
      setStatus('success');
      setMessage(res.data.message || 'Email verified successfully.');
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'verifying' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Loader2 size={24} className="text-blue-600 animate-spin" />
              </div>
              <CardTitle className="text-xl">Verifying Email</CardTitle>
              <CardDescription>Please wait while we verify your email address...</CardDescription>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <ShieldCheck size={24} className="text-green-600" />
              </div>
              <CardTitle className="text-xl">Email Verified</CardTitle>
              <CardDescription>Your email has been confirmed</CardDescription>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-2">
                <XCircle size={24} className="text-red-600" />
              </div>
              <CardTitle className="text-xl">Verification Failed</CardTitle>
            </>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm space-y-3">
              <div className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                <p>{message}</p>
              </div>
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p>An INMX administrator will now review your registration and approve your access. You will receive a notification once your account is active.</p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm">
              <p className="text-red-700">{message}</p>
            </div>
          )}

          <div className="pt-2 border-t text-center">
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="go-to-login">
              <ArrowLeft size={14} className="mr-1" /> Go to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
